(() => {
  const configEl = document.getElementById('free-gift-config');
  if (!configEl) return;

  const enabled = configEl.dataset.freeGiftEnabled === 'true' || configEl.dataset.freeGiftEnabled === '1';
  const variantId = parseInt(configEl.dataset.freeGiftVariantId || '0', 10);
  const threshold = parseInt(configEl.dataset.freeGiftThreshold || '7500', 10); // cents

  if (!enabled || !variantId || !threshold) return;
  const LOADER_MIN_DURATION = 1000;

  let busy = false;
  let pendingCheck = false;
  const STORAGE = window.sessionStorage;
  const MANUAL_BLOCK_KEY = 'fg_manual_block_sig';
  const LAST_SIG_KEY = 'fg_last_sig';
  const LAST_HAD_GIFT_KEY = 'fg_last_had_gift';

  function startDrawerLoading(minDuration) {
    try {
      document.dispatchEvent(new CustomEvent('cart:drawer-loading-start', {
        detail: { minDuration: typeof minDuration === 'number' ? minDuration : LOADER_MIN_DURATION }
      }));
    } catch (_e) {}
  }

  function stopDrawerLoading() {
    try {
      document.dispatchEvent(new CustomEvent('cart:drawer-loading-stop'));
    } catch (_e) {}
  }

  function signatureFromCart(cart) {
    try {
      const parts = cart.items
        .filter(i => i.variant_id !== variantId) // ignore gift itself
        .map(i => `${i.variant_id}:${i.quantity}`)
        .sort();
      return parts.join('|');
    } catch (_e) { return ''; }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  function getAutoGiftLines(cart) {
    return cart.items.filter(
      (i) => i.variant_id === variantId && i.properties && (i.properties._free_gift === 'true' || i.properties._free_gift === true)
    );
  }

  function getAllGiftVariantLines(cart) {
    return cart.items.filter((i) => i.variant_id === variantId);
  }

  function findAutoGiftLineKey(cart) {
    const item = cart.items.find((i) => i.properties && (i.properties._free_gift === 'true' || i.properties._free_gift === true));
    return item ? item.key : null;
  }

  async function addGift() {
    // guard against rapid double-adds
    if (STORAGE.getItem('fg_adding') === '1') return;
    STORAGE.setItem('fg_adding', '1');
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _free_gift: 'true' }
      })
    });
    // clear adding flag shortly after
    setTimeout(() => { try { STORAGE.removeItem('fg_adding'); } catch (_e) {} }, 400);
  }

  async function removeGift(lineKey) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
  }

  async function changeGiftQty(lineKey, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity })
    });
  }

  async function refreshCartUI() {
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    try {
      const res = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`, { credentials: 'same-origin' });
      if (res.ok) {
        const data = await res.json();
        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl && typeof drawerEl.renderContents === 'function') {
          try { drawerEl.renderContents({ sections: data }); return; } catch (_e) {}
        }
      }
    } catch (_e) {}

    try {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (_e2) {}
  }

  async function checkFreeGift() {
    if (busy) {
      pendingCheck = true;
      return;
    }
    busy = true;
    pendingCheck = false;
    let overlayShown = false;
    const ensureLoading = (minDuration) => {
      if (overlayShown) return;
      overlayShown = true;
      startDrawerLoading(minDuration);
    };
    try {
      const cart = await fetchCart();
      const total = cart.total_price || 0; // after discounts, cents
      const autoGiftLines = getAutoGiftLines(cart);
      const allGiftVariantLines = getAllGiftVariantLines(cart);
      const present = autoGiftLines.length > 0;
      const presentAny = allGiftVariantLines.length > 0;
      const giftKey = autoGiftLines[0]?.key || findAutoGiftLineKey(cart);
      let mutated = false;

      // Deduplicate and cap quantity to 1 for auto-added gift
      if (autoGiftLines.length > 1) {
        ensureLoading();
        for (let idx = 1; idx < autoGiftLines.length; idx++) {
          try { await removeGift(autoGiftLines[idx].key); mutated = true; } catch (_e) {}
        }
      }
      if (autoGiftLines.length >= 1 && autoGiftLines[0].quantity > 1) {
        ensureLoading();
        try { await changeGiftQty(autoGiftLines[0].key, 1); mutated = true; } catch (_e) {}
      }
      // If both paid and auto-gift lines exist, ensure only one auto-gift line remains
      if (allGiftVariantLines.length > 1 && autoGiftLines.length > 1) {
        ensureLoading();
        for (let idx = 1; idx < autoGiftLines.length; idx++) {
          try { await removeGift(autoGiftLines[idx].key); mutated = true; } catch (_e) {}
        }
      }
      if (mutated) {
        ensureLoading();
        await refreshCartUI();
        // Update last state markers after mutation
        const newCart = await fetchCart();
        const newSig = signatureFromCart(newCart);
        STORAGE.setItem(LAST_HAD_GIFT_KEY, getAutoGiftLines(newCart).length > 0 ? '1' : '0');
        STORAGE.setItem(LAST_SIG_KEY, newSig);
        return;
      }
      const sig = signatureFromCart(cart);
      const manualBlockSig = STORAGE.getItem(MANUAL_BLOCK_KEY);
      const lastHadGift = STORAGE.getItem(LAST_HAD_GIFT_KEY) === '1';
      const lastSig = STORAGE.getItem(LAST_SIG_KEY) || '';

      // If gift disappeared while still eligible, assume manual removal and block re-adding for this signature
      if (lastHadGift && !present && total >= threshold) {
        STORAGE.setItem(MANUAL_BLOCK_KEY, sig);
      }
      // If cart items changed (ignoring gift), clear any manual block
      if (manualBlockSig && manualBlockSig !== sig) {
        STORAGE.removeItem(MANUAL_BLOCK_KEY);
      }

      if (total >= threshold && !presentAny) {
        const blocked = STORAGE.getItem(MANUAL_BLOCK_KEY);
        if (!blocked || blocked !== sig) {
          ensureLoading();
          await addGift();
          // clear manual block once we add
          STORAGE.removeItem(MANUAL_BLOCK_KEY);
          await refreshCartUI();
        }
      } else if (total < threshold && giftKey) {
        ensureLoading();
        await removeGift(giftKey);
        await refreshCartUI();
      }

      // Update last state
      STORAGE.setItem(LAST_HAD_GIFT_KEY, present ? '1' : '0');
      STORAGE.setItem(LAST_SIG_KEY, sig);
    } catch (e) {
      console.warn('Free gift check failed', e);
    } finally {
      if (overlayShown) stopDrawerLoading();
      busy = false;
      if (pendingCheck) {
        pendingCheck = false;
        setTimeout(checkFreeGift, 0);
      }
    }
  }

  // Intercept cart-related fetch requests
  const originalFetch = window.fetch.bind(window);
  window.fetch = function(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isCartEndpoint = /\/cart\/(add|change|update|clear)\.js|\/cart\.js|\/cart\/add/.test(url);
    const result = originalFetch(input, init);
    if (isCartEndpoint) {
      result.then(() => setTimeout(checkFreeGift, 75)).catch(() => {});
    }
    return result;
  };

  // Hook add-to-cart form submits
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form && form.action && /\/cart\/add/.test(form.action)) {
      setTimeout(checkFreeGift, 150);
    }
  });

  document.addEventListener('DOMContentLoaded', checkFreeGift);
  document.addEventListener('cart:open', checkFreeGift);
  document.addEventListener('cart:refresh', checkFreeGift);
})();
